import { whenActivated } from './prerender.js';
import { isTenantlessHost } from './host.js';

// Instant portal (docs/KB.md gotcha 25): stale-while-revalidate from a copy on THIS device.
// The portal's first frame used to wait for /api/me, then the page chunk, then the ticket
// list — a spinner, then a "Loading…" row. A returning viewer now gets the last-seen state
// at once: App renders from the cached /api/me chrome, the queue / the customer's list from
// the cached rows, and both are replaced IN PLACE when the real responses land (same keys,
// no placeholder, no flicker). Cold visits (nothing cached) behave exactly as before.
//
// What is kept (localStorage — read synchronously, so the very first render can use it):
//   ONE entry per origin (= per workspace host): { v, w, ws, sub, at, me, views, people }
//   · ws + sub = the workspace id and the signed-in subject ('a:<agentId>' | 'c:<endUserId>')
//     it belongs to. /api/me naming anything else DISCARDS it (confirm()) — and App keys the
//     page by subject, so a different person's rows are never mixed into a live page.
//   · me = an ALLOWLISTED projection of /api/me — what the header, menus and list need
//     (CHROME_KEYS). Never a token (auth is an httpOnly cookie; /api/me carries none), never
//     the pending OAuth request, the MCP address, billing, or anything not listed.
//   · views = the first page of rows per list view (agent: status/substate/assignee; the
//     customer's single list), projected to what a ROW SHOWS (projectRow) — no message,
//     body, preview or e-mail. Assignee avatars are stored once in `people`.
//   · at = last write. Stale after TTL_MS (24 h): not used, and removed. w = a random token
//     per write (how a tab notices that another tab wrote since it last looked).
// When it is used for a first render: fresh, the right version, a tenant host, and NOT a
// navigation that arrived through a redirect — the magic-link confirm (303), SSO and every
// other server sign-in land on the portal that way, possibly as somebody else in a shared
// browser; those boot cold and /api/me decides (their rows are then this subject's own).
// Purged on every sign-out path, on a 401 (App's unauthorized handler), on any /api/me that
// is not a signed-in agent/customer (signed out, mid-2FA, forced enrolment), on the /login/
// page, before an identity switch, and after an account deletion.
// Prerender (gotcha 24): reads are fine; WRITES wait for activation (whenActivated) — a
// hidden page that may be discarded stores nothing. A PURGE never waits: it only removes,
// and deferring it would leave a signed-out session's rows behind if the prerender is
// discarded.
// Several tabs: the entry is shared. Another tab purging it or filing it under a different
// subject (a sign-out / sign-in there) stops this tab writing until its own /api/me
// confirms someone again (the `storage` event, plus a re-read before every write: the stored
// `w` must still be the one this tab last saw); the same subject's newer copy is adopted.

const KEY = 'es_boot';
const VERSION = 1;
export const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_VIEWS = 4; // the most recently seen list views (filter combinations)
export const ROW_CAP = { agent: 50, customer: 100 };
const WRITE_DELAY_MS = 400; // live refreshes arrive in bursts — one write per burst
const CHROME_KEYS = ['workspace', 'agent', 'endUser', 'alsoCustomer', 'alsoAgent', 'alsoAgentPasskey', 'capabilities', 'health'];

const ok = () => typeof window !== 'undefined' && !isTenantlessHost();
const now = () => Date.now();

// 'a:<id>' | 'c:<id>' | null — the SUBJECT the portal renders for (agent precedence, like App).
export function subjectOf(me) {
  if (me?.agent?.id) return `a:${me.agent.id}`;
  if (me?.endUser?.id) return `c:${me.endUser.id}`;
  return null;
}
// A signed-in portal viewer whose console renders (not mid-2FA, not walled into enrolment).
const cacheable = (me) => !!(me?.workspace?.id && subjectOf(me) && !me.mfaPending && !me.mustEnrollMfa
  && !me.mustEnrollPasskey && !me.signup && !me.platformAdmin && !me.sandbox);

function chromeOf(me) {
  const out = {};
  for (const k of CHROME_KEYS) if (me[k] !== undefined) out[k] = me[k];
  return out;
}

// A list row, reduced to what TicketRow draws + what the facet filters read.
function projectRow(t, people) {
  if (!t?.id) return null;
  const row = { id: t.id, ref: t.ref, subject: t.subject, status: t.status, lastMessageAt: t.lastMessageAt };
  if (t.priority !== undefined) { // agent rows
    Object.assign(row, {
      substate: t.substate, priority: t.priority, typeId: t.typeId || null, typeName: t.typeName || null,
      typeColor: t.typeColor || null, typeSystemKey: t.typeSystemKey || null, typeNameTranslations: t.typeNameTranslations || null,
      customer: t.customer ? { name: t.customer.name || '' } : null
    });
    if (t.assignee?.id) {
      people[t.assignee.id] = { n: t.assignee.displayName || '', a: t.assignee.avatarUrl || null };
      row.as = t.assignee.id;
    }
  }
  return row;
}
function expandRows(rows, people) {
  const who = {};
  const person = (id) => (who[id] ||= { id, displayName: people[id]?.n || '', avatarUrl: people[id]?.a || null });
  return rows.map(({ as, ...r }) => (as ? { ...r, assignee: person(as) } : (r.priority !== undefined ? { ...r, assignee: null } : r)));
}

function readStored() {
  try {
    const e = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!e || e.v !== VERSION || typeof e.sub !== 'string' || typeof e.ws !== 'string' || !e.me || typeof e.at !== 'number') return e ? false : null;
    if (now() - e.at > TTL_MS) return false;
    e.views ||= {};
    e.people ||= {};
    return e;
  } catch { return false; }
}
function arrivedViaRedirect() {
  try { return (window.performance.getEntriesByType('navigation')[0]?.redirectCount || 0) > 0; } catch { return false; }
}

let entry = null;       // the in-memory mirror of the stored entry
let seen = null;        // the `w` of the stored entry as this tab last read / wrote it
let usable = false;     // may `entry` feed a render before /api/me confirms it?
let confirmed = null;   // the subject /api/me confirmed in THIS document
let timer = null;
const pending = new Map(); // viewKey → rows fetched before /api/me confirmed the subject

function removeStored() {
  try { window.localStorage.removeItem(KEY); } catch { /* storage blocked */ }
}

// Evaluated once, at module load (main.jsx, before the first render).
function init() {
  if (!ok()) return null;
  const e = readStored();
  if (e === false) { removeStored(); return null; } // stale / foreign shape → gone (seen stays null)
  if (!e) return null;
  entry = e;
  seen = e.w || null;
  if (arrivedViaRedirect()) return null; // a sign-in landing: boot cold, let /api/me decide
  usable = true;
  return { me: e.me, kind: e.sub.startsWith('a:') ? 'agent' : 'customer' };
}
// { me, kind } — the cached chrome for an instant first render — or null (boot cold).
export const boot = init();

if (ok()) {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY && e.key !== null) return;
    const other = readStored();
    if (other && entry && other.sub === entry.sub && other.ws === entry.ws) { entry = other; seen = other.w || null; return; }
    // Purged, or somebody else's now: this document keeps what it shows, but stores nothing
    // (and serves no cached view) until its own /api/me confirms a viewer again.
    seen = other ? other.w || null : null;
    entry = null;
    usable = false;
    confirmed = null;
    pending.clear();
    if (timer) { clearTimeout(timer); timer = null; }
  });
}

function persist() {
  timer = null;
  if (!entry || !confirmed || entry.sub !== confirmed) return;
  // Another tab changed it since we last looked — purged it, or filed someone else's: leave
  // it to them (the `storage` event normally got here first).
  const stored = readStored();
  if ((stored ? stored.w || null : null) !== seen && !(stored && stored.sub === entry.sub && stored.ws === entry.ws)) {
    seen = stored ? stored.w || null : null;
    entry = null; usable = false; confirmed = null; return;
  }
  // Drop expired and surplus views, then the avatars nothing references any more.
  const views = Object.entries(entry.views).filter(([, v]) => now() - v.at <= TTL_MS)
    .sort((a, z) => z[1].at - a[1].at).slice(0, MAX_VIEWS);
  entry.views = Object.fromEntries(views);
  const used = new Set(views.flatMap(([, v]) => v.rows.map((r) => r.as).filter(Boolean)));
  entry.people = Object.fromEntries(Object.entries(entry.people).filter(([id]) => used.has(id)));
  entry.w = Math.random().toString(36).slice(2);
  for (let keep = views.length; keep >= 0; keep--) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ ...entry, views: Object.fromEntries(views.slice(0, keep)) }));
      seen = entry.w;
      return;
    } catch { /* quota: retry with fewer views */ }
  }
  removeStored();
  seen = null;
}
function schedule() {
  if (timer) return;
  timer = setTimeout(() => { whenActivated().then(persist); }, WRITE_DELAY_MS);
}

function putView(viewKey, kind, { total, tickets }) {
  const rows = (tickets || []).slice(0, ROW_CAP[kind] || 50).map((t) => projectRow(t, entry.people)).filter(Boolean);
  entry.views[viewKey] = { at: now(), total: Number.isFinite(total) ? total : rows.length, rows };
}

// /api/me landed (App.refresh). A signed-in viewer: the entry becomes theirs — kept (chrome
// updated) when it already was, replaced (every cached row dropped) when it named anyone
// else. Anything else (signed out, mid-2FA, enrolment wall) purges.
export function confirm(me) {
  if (!ok()) return;
  if (!cacheable(me)) { purge(); return; }
  const sub = subjectOf(me);
  const ws = String(me.workspace.id);
  if (!entry || entry.sub !== sub || entry.ws !== ws) {
    entry = { v: VERSION, ws, sub, at: now(), me: chromeOf(me), views: {}, people: {} };
    pending.clear(); // fetched for a page keyed to the old subject — that page is remounting
  } else {
    entry.me = chromeOf(me);
    entry.at = now();
  }
  confirmed = sub;
  usable = true;
  for (const [viewKey, p] of pending) putView(viewKey, p.kind, p.data);
  pending.clear();
  schedule();
}

// The cached rows for a list view ({ total, tickets }), or null.
export function cachedList(viewKey) {
  if (!entry || !usable || (confirmed && entry.sub !== confirmed)) return null;
  const v = entry.views[viewKey];
  if (!v || now() - v.at > TTL_MS || !Array.isArray(v.rows)) return null;
  return { total: v.total, tickets: expandRows(v.rows, entry.people) };
}

// A fresh list response for a view (the first page of it is kept).
// Before /api/me has confirmed who this is (a cached first render fetches the list in
// parallel with it), the rows wait in memory; confirm() files them under the right subject.
export function rememberList(viewKey, kind, data) {
  if (!ok()) return;
  if (!confirmed) { pending.set(viewKey, { kind, data }); return; }
  if (!entry || entry.sub !== confirmed) return;
  putView(viewKey, kind, data);
  schedule();
}

// Forget everything, now (sign-out, 401, a different/absent subject, account deletion).
export function purge() {
  if (timer) { clearTimeout(timer); timer = null; }
  entry = null;
  usable = false;
  confirmed = null;
  pending.clear();
  seen = null;
  if (typeof window !== 'undefined') removeStored();
}

// Is there still a cached entry behind a render that came from it? (App keeps showing the
// cached page when /api/me merely FAILS — offline — but not after a purge.)
export const isLive = () => !!entry && usable;

// The list-view key: agents per server-side filter (text/token filters are client-side and
// apply to whatever rows are shown), customers have one list.
export const agentViewKey = (t) => `a:${t.status}:${t.status === 'closed' ? '' : t.substate}:${t.assignment}`;
export const CUSTOMER_VIEW = 'c';
